import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "./lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-150 focus-visible:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Brand primary variant (default CTA with elevation)
        primary:
          "bg-primary text-primary-foreground shadow-sm hover:shadow btn-elevation-default hover:bg-primary/90 hover:btn-elevation-hover active:btn-elevation-active active:scale-[0.98]",
        // Legacy alias for primary while downstream apps migrate away from `default`
        default:
          "bg-primary text-primary-foreground shadow-sm hover:shadow btn-elevation-default hover:bg-primary/90 hover:btn-elevation-hover active:btn-elevation-active active:scale-[0.98]",
        // Brand accent variant (secondary actions with elevation)
        accent:
          "bg-accent text-accent-foreground shadow-sm hover:shadow btn-elevation-default hover:bg-accent/90 hover:btn-elevation-hover active:btn-elevation-active active:scale-[0.98]",
        // Destructive/error actions
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        // Outline variant with brand border
        outline:
          "border-2 border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        // Secondary variant (subtle actions)
        secondary: "bg-secondary text-secondary-foreground border-2 shadow-sm hover:bg-secondary/80",
        // Ghost variant (minimal visual weight)
        ghost: "hover:bg-accent hover:text-accent-foreground",
        // Link variant (no background)
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-5 py-2.5",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-10 rounded-lg px-8",
        icon: "h-9 w-9",
      },
      elevation: {
        none: "",
        default: "btn-elevation-default hover:btn-elevation-hover active:btn-elevation-active",
        always: "btn-elevation-md",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
      elevation: undefined,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, elevation, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, elevation, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
